import { Checkbox, Icon, Icons, Intent, Size } from "construct-ui";
import codeMasterModel from "../../models/common/code-master.model";
import loginModel from "../../models/login.model";
import RawDataModel from "../../models/raw-data/raw-data.model";
import ReportModel from "../../models/report/report-model.model";
import ViewBase from "../../views/view-base.view";
import CodeByCategorySelectionComponent from "../codemaster/codemaster-category-search-select.component";
import ToggleButtonGroupComponent, { BUTTON_TYPE } from "../common/toggle-button-group.component";
import MultiSelectComponent from "../multi-select/multi-select.component";
import SingleSelectComponent from "../single-select/single-select.component";
import CodemasterCategorySearchTree from "../codemaster/codemaster-category-search-tree.component";

/**
 * DynamicReportFilterComponent
 * 
 * Dynamic search filters for reports
 * 
 * @author CV Leong
 */
export default function DynamicReportFilterComponent() {
  ViewBase.call(this);

  const CONFIG_TYPES = {
    CHECKBOX: 'check',
    NUMBER: 'number',
    RADIO: 'radio',
    SEARCH: 'search',
    SELECT_MULTIPLE: '',
    SELECT_SINGLE: 'ddl',
    TEXT: 'txt',
    TREE: 'tree'
  }

  const COMPONENT_ID = `DynamicReportFilterComponent-${uniqueInstance++}`;
  const ALLOWED_FILTER_CONFIG = Object.values(CONFIG_TYPES);
  const KEYS_SEPARATOR = '^^';
  const KEY_VALUE_SEPARATOR = ':=';
  const VALUES_SEPARATOR = '^';

  const cachedFilterTypes = new Map(); // Cached filter type lists

  let filterTypes = [];
  let isLoadedFromTemplate = false;
  let selectedFilterOptionParams = [];
  let handleOnChange = (data) => { console.log(data) };

  const inputParams = {
    // Report Params
    dataSourceCode: '',
    periodCode: '',
    parameters: [],
    // Raw Data Params
    dataTypeId: '',
    location: '',
  }

  const getFilterComponentId = (data) => {
    return `${COMPONENT_ID}-${data.config ?? ''}-${data.code ?? ''}`
  }

  const haveSourceParamsChanged = (dataSourceCode, periodCode, dataTypeId) => {
    return (inputParams.dataSourceCode !== dataSourceCode
      || inputParams.periodCode !== periodCode
      || inputParams.dataTypeId !== dataTypeId);
  }

  // Context: Unlike others, options here means options of an filter type
  const extractOptions = (options = '') => {
    if (!options) {
      return undefined;
    }

    const optionList = [];
    const arr = options.split(KEYS_SEPARATOR);

    arr.map(o => {
      optionList.push({
        code: o.split(VALUES_SEPARATOR)[0],
        name: o.split(VALUES_SEPARATOR)[1] || o.split(VALUES_SEPARATOR)[0],
      })
    })

    return optionList;
  }

  const transformFilterTypeObjects = (filterTypes = []) => {
    let types = filterTypes.filter(t => ALLOWED_FILTER_CONFIG.includes(t.config));
    return types.map(t => ({
      ...t,
      options: extractOptions(t.options),
      name: t.name?.split(VALUES_SEPARATOR)[0] || '',
      dataType: t.name?.split(VALUES_SEPARATOR)[1] || '',
    }))
  }

  const getFilterTypeList = ({ dataSourceCode, periodCode }) => {
    if (!dataSourceCode || !periodCode) return Promise.resolve([]);
    // Get cached option list
    let cachedOptions = cachedFilterTypes.get(`${loginModel.credentials.country}-${dataSourceCode}-${periodCode}`);
    if (cachedOptions) {
      return Promise.resolve(cachedOptions);
    };

    // Get option list via API
    this.showLoading();

    return ReportModel.getReportFilter(dataSourceCode, periodCode)
      .then((result) => {
        let options = result || [];
        cachedFilterTypes.set(`${loginModel.credentials.country}-${dataSourceCode}-${periodCode}`, options);
        return options;
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  const getRawDataFilters = (dataTypeId, location) => {
    if (!dataTypeId) return Promise.resolve([]);

    // Get cached option list
    let cachedOptions = cachedFilterTypes.get(`${loginModel.credentials.country}-${dataTypeId}`);
    if (cachedOptions) {
      return Promise.resolve(cachedOptions);
    };

    // Get option list via API
    this.showLoading();

    return RawDataModel.getFilters(dataTypeId, location)
      .then(result => {
        let options = result || [];
        cachedFilterTypes.set(`${loginModel.credentials.country}-${dataTypeId}`, options);
        return options;
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  const addFilterElement = (option) => {
    if (!selectedFilterOptionParams.some(param => param.key === option.code)) {
      setParamValues(option.code, option.default || '');
    }
  }

  const handleSelectFilter = (filter) => {
    addFilterElement(filter);
  }

  const isOptionNotSelected = (option) => {
    return !selectedFilterOptionParams.some(selected => selected.key === option.code);
  }

  const getParamValues = (key, configType) => {
    let value = selectedFilterOptionParams.find(p => p.key === key)?.value;

    switch (configType) {

      case CONFIG_TYPES.CHECKBOX:
        if (typeof value === 'boolean') {
          return value;
        }

        if (typeof value === 'string') {
          if (value) {
            return value.toLowerCase() === 'true' || value === '1';
          }
        }

        if (typeof value === 'number') {
          if (value === 0) return false;
          if (value === 1) return true;
          // else ignore invalid numbers
        }

        return false;

      case CONFIG_TYPES.SEARCH:
        // Make sure an array of object(s) is returned
        if (!Array.isArray(value)) {
          if (value) {
            if (typeof value === 'string') {
              return [{
                code: value,
                name: value
              }];
            }

            // Assumption: `value` is an object `{ code, name }`
            return [value];
          }
          return [];
        }

        // Return an array of object(s)
        return value.map((item) => {
          if (typeof item === 'string') {
            return {
              code: item,
              name: item
            }
          }

          // Assumption: `item` is an object `{ code, name }`
          return item;
        })
      case CONFIG_TYPES.TREE:
      case CONFIG_TYPES.SELECT_MULTIPLE:
        // Make sure an array is returned
        if (!Array.isArray(value)) {
          if (value && typeof value === 'string') {
            return [value];
          }
          return [];
        }
        return value;

      default:
        return value;
    }
  }

  const resetSelectedFilterOptionParams = () => {
    selectedFilterOptionParams = [];
    handleOnChange(selectedFilterOptionParams, isLoadedFromTemplate);
  }

  const setParamValues = (key, value) => {
    const param = selectedFilterOptionParams.find(p => p.key === key);
    if (param) {
      param.value = value;
    } else {
      selectedFilterOptionParams.push({ key, value })
    }

    handleOnChange(selectedFilterOptionParams, isLoadedFromTemplate);
  }

  const removeParam = (key) => {
    selectedFilterOptionParams = selectedFilterOptionParams.filter(p => p.key !== key);
    handleOnChange(selectedFilterOptionParams, isLoadedFromTemplate);
    return selectedFilterOptionParams;
  }

  const removeFilterComponent = (dataCode) => {
    removeParam(dataCode);
  }

  const DEFAULT_WRAPPER = () => {
    return {
      view: (vnode) => {
        return [
          m('', vnode.attrs, vnode.children)
        ]
      }
    }
  }

  const FilterRadio = () => {
    let options = [];
    return {
      oninit: ({ attrs: { data, onChange, value } }) => {
        options = data.options ? [...data.options] : [];

        if (!value) {
          value = options[0]?.code;
          onChange(value);
        }
      },
      view: ({ attrs: { data, onChange, required, value } }) => {
        return [
          m(ToggleButtonGroupComponent, {
            ariaLabel: data.name,
            buttonType: BUTTON_TYPE.RADIO,
            buttons: options.map((o, index) => ({
              checked: value ? o.code === value : index === 0,
              id: `${getFilterComponentId(data)}--${o.code}`,
              label: o.name,
              value: o.code,
            })),
            name: data.name,
            required,
            onChange: (e) => {
              onChange(e.target.value);
            },
          }),
        ]
      }
    }
  }

  const FilterMultiSelect = () => {
    let options = [];

    const isKeyOnly = (value) => {
      if (Array.isArray(value)) {
        return !value.at(0)?.hasOwnProperty('code')
      }

      return true
    }

    const convertValueStringToArray = (value) => {
      if (typeof value === 'string') {
        return [value];
      }

      return value
    }

    return {
      oninit: ({ attrs: { data } }) => {
        codeMasterModel.searchByCategoryV2(data.dataType)
          .then(_data => {
            if (_data) {
              options = [..._data];
            }
          })
      },
      view: ({ attrs: { id, onSelect, required, selectedItems } }) => {
        return [
          m(MultiSelectComponent, {
            id: id,
            items: options,
            keyOnly: isKeyOnly(selectedItems),
            required: required,
            selectedItems: convertValueStringToArray(selectedItems),
            onSelect,
          })
        ]
      }
    }
  }

  const FilterSingleSelect = () => {
    let options = [];

    const isKeyOnly = (value) => {
      if (typeof value === 'string') {
        return true
      }

      return false
    }

    return {
      oninit: ({ attrs: { data } }) => {
        options = data.options
          ? [...data.options]
          : codeMasterModel.searchByCategoryV2(data.dataType)
            .then(_data => {
              if (_data) {
                options = [..._data];
              }
            });
      },
      view: ({ attrs: { id, onSelect, required, selectedItem } }) => {
        return [
          m(SingleSelectComponent, {
            id: id,
            items: options,
            keyOnly: isKeyOnly(selectedItem),
            required: required,
            selectedItem,
            onSelect,
          })
        ]
      }
    }
  }

  const FilterComponent = () => {
    return {
      view: ({ attrs: { data } }) => {
        return [
          // Label (hidden for checkbox)
          m('.d-flex.justify-content-between',
            CONFIG_TYPES.CHECKBOX !== data.config
              ? m('label.form-label', {
                class: data.required ? 'mandatory' : undefined,
                for: getFilterComponentId(data),
              }, data.name)
              : m(''),
            !data.required &&
            m(Icon, {
              intent: Intent.NEGATIVE,
              name: Icons.MINUS_CIRCLE,
              onclick: () => removeFilterComponent(data.code),
              size: Size.SM,
              title: this.getText('LT00000237', 'Remove'),
            }),
          ),

          // Checkbox input
          CONFIG_TYPES.CHECKBOX === data.config && [
            m(Checkbox, {
              checked: getParamValues(data.code, CONFIG_TYPES.CHECKBOX),
              id: getFilterComponentId(data),
              label: data.name,
              onchange: (e) => {
                setParamValues(data.code, e.target.checked);
              },
            }),
          ],

          // Radio input
          CONFIG_TYPES.RADIO === data.config && [
            m('.position-relative',
              m(FilterRadio, {
                data: data,
                required: data.required,
                value: getParamValues(data.code, CONFIG_TYPES.RADIO),
                onChange: (value) => {
                  setParamValues(data.code, value);
                }
              }),
              m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
            )
          ],

          // Popup, search and select
          CONFIG_TYPES.SEARCH === data.config && [
            m('.position-relative',
              m(CodeByCategorySelectionComponent, {
                title: data.name,
                category: data.dataType,
                required: data.required,
                selectedItems: getParamValues(data.code, CONFIG_TYPES.SEARCH),
                onSelect: (selectedItems) => {
                  setParamValues(data.code, selectedItems);
                }
              }),
              m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
            )
          ],

          // Multi-select
          CONFIG_TYPES.SELECT_MULTIPLE === data.config && [
            m('.position-relative',
              m(FilterMultiSelect, {
                id: getFilterComponentId(data),
                data,
                required: data.required,
                selectedItems: getParamValues(data.code, CONFIG_TYPES.SELECT_MULTIPLE),
                onSelect: (selected) => {
                  setParamValues(data.code, selected);
                }
              }),
              m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
            )
          ],

          // Single select
          CONFIG_TYPES.SELECT_SINGLE === data.config && [
            m('.position-relative',
              m(FilterSingleSelect, {
                id: getFilterComponentId(data),
                data,
                required: data.required,
                selectedItem: getParamValues(data.code, CONFIG_TYPES.SELECT_SINGLE),
                onSelect: (selected) => {
                  setParamValues(data.code, selected);
                }
              }),
              m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
            )
          ],

          // Number / Text input
          [CONFIG_TYPES.NUMBER, CONFIG_TYPES.TEXT].includes(data.config) && [
            m('.position-relative',
              m('input.form-control', {
                id: getFilterComponentId(data),
                onchange: (e) => {
                  setParamValues(data.code, e.target.value);
                },
                inputmode: data.config === CONFIG_TYPES.NUMBER ? 'decimal' : 'text',
                placeholder: data.name,
                required: data.required,
                step: data.config === CONFIG_TYPES.NUMBER ? 'any' : undefined,
                type: data.config === CONFIG_TYPES.NUMBER ? 'number' : 'text',
                value: getParamValues(data.code, data.config),
              }),
              m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
            )
          ],

          // Tree input
          CONFIG_TYPES.TREE === data.config && [
            m('.position-relative',
              m(CodemasterCategorySearchTree, {
                title: data.name,
                category: data.dataType,
                required: data.required,
                fromLevel: data.options?.at(0)?.code,
                toLevel: data.options?.at(1)?.code,
                selectedItems: getParamValues(data.code, CONFIG_TYPES.TREE),
                onChange: (selectedParentCodes, selectedChildCodes) => {
                  setParamValues(data.code, selectedChildCodes);
                }
              }),
              m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
            )
          ],
        ]
      }
    }
  }

  const refreshFilters = (isRawData = false) => {
    // Reset previous data
    resetSelectedFilterOptionParams();

    (isRawData ? getRawDataFilters(inputParams.dataTypeId, inputParams.location) : getFilterTypeList(inputParams))
      .then((data) => {
        filterTypes = transformFilterTypeObjects(data);
        selectAllRequiredFilters(); // Select all required filters
      })
      .catch((error) => {
        filterTypes = [];
        this.handleError(error);
      })
      .finally(() => {
        m.redraw();
      });
  }

  const refreshFiltersForTemplate = (param) => {
    selectedFilterOptionParams = param
    getFilterTypeList(inputParams)
      .then((data) => {
        filterTypes = transformFilterTypeObjects(data);
        selectAllRequiredFilters(); // Select all required filters
        isLoadedFromTemplate = false;
      })
      .catch((error) => {
        filterTypes = [];
        isLoadedFromTemplate = false;
        this.handleError(error);
      })
      .finally(() => {
        handleOnChange(selectedFilterOptionParams, false)
        m.redraw();
      });
  }

  const selectAllRequiredFilters = () => {
    // Auto-add *required* filters
    filterTypes.forEach(option => {
      if (option.required === true) {
        addFilterElement(option)
      };
    });
  }

  const isValidOption = (key) => {
    return !!filterTypes.find(o => o.code == key)
  }

  return {
    oninit: ({ attrs: { onChange = handleOnChange, data: { dataSourceCode = '', periodCode = '', dataTypeId = '', location = '' }, isRawData = false } }) => {
      handleOnChange = onChange;

      if (haveSourceParamsChanged(dataSourceCode, periodCode, dataTypeId)) {
        // Update to current dataSource & period
        inputParams.dataSourceCode = dataSourceCode;
        inputParams.periodCode = periodCode;
        inputParams.dataTypeId = dataTypeId;
        inputParams.location = location;

        refreshFilters(isRawData);
      }
    },
    onupdate: ({ attrs: {
      data: {
        parameters = selectedFilterOptionParams,
        dataSourceCode = '',
        periodCode = '',
        isLoadedFromTemplate: _isLoadedFromTemplate = false, // rename and set default value to false
        dataTypeId = '',
        location = ''
      },
      isRawData = false
    } }) => {
      // _isLoadedFromTemplate: from parent component 
      // isLoadedFromTemplate: local variable
      isLoadedFromTemplate = _isLoadedFromTemplate;
      if (haveSourceParamsChanged(dataSourceCode, periodCode, dataTypeId)) {
        // Update to current dataSource & period
        inputParams.dataSourceCode = dataSourceCode;
        inputParams.periodCode = periodCode;
        inputParams.dataTypeId = dataTypeId;
        inputParams.location = location;

        if (!isLoadedFromTemplate) {
          refreshFilters(isRawData);
        } else {
          isLoadedFromTemplate = false
        }
      }
      // add new logic here
      if (isLoadedFromTemplate) {
        refreshFiltersForTemplate(parameters)
      }
    },
    view: ({
      attrs: {
        buttonWrapperComponent = DEFAULT_WRAPPER,
        filterWrapperComponent = DEFAULT_WRAPPER,
        // data: { parameters = selectedFilterOptionParams },
      }
    }) => {

      /* comment out the old logic, modified by molly */
      // selectedFilterOptionParams = parameters || [];
      // selectAllRequiredFilters(); // Ensure *required* fields are re-added after the form is cleared
      const dropdownFilterOptions = filterTypes.filter(isOptionNotSelected);

      return [
        selectedFilterOptionParams?.map((_filter) => [
          isValidOption(_filter.key) &&
          m(filterWrapperComponent, { key: _filter.key },
            m(FilterComponent, { data: filterTypes.find(o => o.code == _filter.key) })
          )
        ]),
        dropdownFilterOptions.length > 0 && m(buttonWrapperComponent,
          m(SingleSelectComponent, {
            id: `${COMPONENT_ID}-filters`,
            items: dropdownFilterOptions,
            checkmark: true,
            filterable: true,
            isButton: true,
            allowEmpty: false,
            buttonAttrs: {
              label: this.getText("LT00000963", "Add Filter"),
              fluid: false,
              iconLeft: Icons.PLUS,
              intent: Intent.PRIMARY,
              outlined: true
            },
            onSelect: handleSelectFilter,
          }),
        ),
        this.getBaseViews()
      ]
    }
  }
}

let uniqueInstance = 1;
